import React, { useState, useEffect } from 'react';
import matchService from '../services/matchService';

const MatchForm = ({ match, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    tournament_id: '',
    season_id: '',
    venue_id: '',
    home_team_id: '',
    away_team_id: '',
    match_date: '',
    status: 'scheduled'
  });

  const [teams, setTeams] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadFormData();
  }, []);

  useEffect(() => {
    if (match) {
      setFormData({
        tournament_id: match.tournament_id || '',
        season_id: match.season_id || '',
        venue_id: match.venue_id || '',
        home_team_id: match.home_team_id || '',
        away_team_id: match.away_team_id || '',
        match_date: match.match_date ? new Date(match.match_date).toISOString().slice(0, 16) : '',
        status: match.status || 'scheduled'
      });
    }
  }, [match]);

  const loadFormData = async () => {
    try {
      setLoadingData(true);

      const [teamsRes, tournamentsRes, seasonsRes, venuesRes] = await Promise.all([
        matchService.getTeams(),
        matchService.getTournaments(),
        matchService.getSeasons(),
        matchService.getVenues()
      ]);

      setTeams(teamsRes.data || []);
      setTournaments(tournamentsRes.data || []);
      setSeasons(seasonsRes.data || []);
      setVenues(venuesRes.data || []);
    } catch (err) {
      console.error('Error loading form data:', err);
      setError('Failed to load form data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('_id') ? (value ? parseInt(value) : '') : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      let result;
      if (match) {
        result = await matchService.updateMatch(match.id, formData);
        onSuccess(match.id);
      } else {
        result = await matchService.createMatch(formData);
        onSuccess(result.id || result.data?.id);
      }
    } catch (err) {
      console.error('Error saving match:', err);
      setError(err.response?.data?.error || 'Failed to save match');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {/* Tournament */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tournament *
          </label>
          <select
            name="tournament_id"
            value={formData.tournament_id}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select tournament</option>
            {tournaments.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        {/* Season */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Season *
          </label>
          <select
            name="season_id"
            value={formData.season_id}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select season</option>
            {seasons.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Teams */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Home Team *
          </label>
          <select
            name="home_team_id"
            value={formData.home_team_id}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select home team</option>
            {teams.map(t => (
              <option 
                key={t.id} 
                value={t.id}
                disabled={formData.away_team_id && formData.away_team_id === t.id}
              >
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Away Team *
          </label>
          <select
            name="away_team_id"
            value={formData.away_team_id}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select away team</option>
            {teams.map(t => (
              <option 
                key={t.id} 
                value={t.id}
                disabled={formData.home_team_id && formData.home_team_id === t.id}
              >
                {t.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Venue and Date */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Venue *
          </label>
          <select
            name="venue_id"
            value={formData.venue_id}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select venue</option>
            {venues.map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Match Date *
          </label>
          <input
            type="datetime-local"
            name="match_date"
            value={formData.match_date}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Status (only for edit) */}
      {match && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="scheduled">Scheduled</option>
            <option value="live">Live</option>
            <option value="finished">Finished</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Saving...' : match ? 'Update Match' : 'Create Match'}
        </button>
      </div>
    </form>
  );
};

export default MatchForm;
